import React, { useState, useEffect, useRef } from "react";
import { Card, Button, Form, Row, Col, Modal, Badge } from "react-bootstrap";
import styles from "./CommunicationForm.module.css";
import PatientCard from "../cards/PatientCard";
import EncounterCard from "../cards/EncounterCard";
import ProviderSearch from "../components/ProviderSearch";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { addMessage } from "../slices/messageSlice"; // Adjust path if needed
import { promotePending } from "../store/thunks/promotePending"; // adjust path as needed
import { fetchProvider } from "../services/providerService";
import { useStore } from "react-redux";
import { logCommEvent } from "../services/logService";
import { FaFilePdf, FaFileImage, FaFileAlt } from "react-icons/fa";
import { createWrappedMessage, buildCommunicationPayload } from "../utils/messageUtils";
import AttachmentBrowserModal from "../modals/AttachmentBrowserModal";

function CommunicationForm({
    contextProvider,
    patientData,
    fetchedEncounter,
    inContext,
    commService,
    recipient,
    replyToCommId,
    fhirClient,
    onCloseForm,
    initialRecipients = [],
    onSendLocalReply,
    selectedComm, //if this is populated, we are replying to an existing message
    isReplyMode

}) {

    //console.log("contextProvider in form:", contextProvider);
    //console.log("patientData in form:", patientData);
    //console.log("fetchedEncounter in form:", fetchedEncounter);
    //console.log("isReplyMode in form:", isReplyMode);     
    //console.log ("selectedComm in form:", selectedComm);
    //console.log("isReplyMode:", isReplyMode);
    const [message, setMessage] = useState("");
    const [priority, setPriority] = useState("routine");
    const [category, setCategory] = useState("notification");
    const [about, setAbout] = useState("");
    const [reason, setReason] = useState("");
    const [error, setError] = useState("");
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [submissionResult, setSubmissionResult] = useState("");
    const defaultTopic = `${(patientData.name?.[0]?.given?.join(" ") + " " + patientData.name?.[0]?.family) || "Progress Update"} - ${fetchedEncounter?.reasonCode?.[0]?.coding?.[0]?.display || fetchedEncounter?.type?.[0]?.text || "Unknown Encounter"}`;
    const [topic, setTopic] = useState(defaultTopic);
    //const [isReply, setIsReply] = useState(!!selectedComm);
    const [selectedProvider, setSelectedProvider] = useState(replyToCommId ? recipient || null : null);
    const [selectedPatient, setSelectedPatient] = useState(patientData || null);
    const [selectedEncounterForModal, setSelectedEncounterForModal] = useState(fetchedEncounter);


    const dispatch = useDispatch();
    const store = useStore();
    const [recipients, setRecipients] = useState(() => replyToCommId && initialRecipients.length > 0 ? initialRecipients : []);
    const providers = useSelector((state) => state.providers);
    const onRecipientsChange = (updatedRecipients) => {
        setRecipients(updatedRecipients);
    };

    const removeRecipient = (id, type) => {
        const updated = recipients.filter(r => !(r.id === id && r.resourceType === type));
        setRecipients(updated);
        if (onRecipientsChange) onRecipientsChange(updated);
    };

    const updateTopicFromEncounter = (encounter) => {
        if (!encounter || !selectedPatient) return;

        const patientName = `${selectedPatient.name?.[0]?.given?.join(" ") ?? ""} ${selectedPatient.name?.[0]?.family ?? ""}`.trim();
        const reason = encounter.reasonCode?.[0]?.coding?.[0]?.display;
        const type = encounter.type?.[0]?.text;
        const newTopic = `${patientName || "Progress Update"} - ${reason || type || "Unknown Encounter"}`;

        setTopic(newTopic);
        console.log("Updated topic:", newTopic);
    };

    const [showAttachments, setShowAttachments] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const handleRemoveAttachment = (index) => {
        const updatedDocs = [...selectedDocuments];
        updatedDocs.splice(index, 1);
        setSelectedDocuments(updatedDocs);
    };

    const fileInputRef = useRef(null);
    //console.log("------------------------------------------", replyToCommId);


    //console.log("Encounter data in form:", fetchedEncounter); //contextEncounter at this point
    //console.log("reply mode", isReplyMode);


    useEffect(() => {
        //console.log("using effect!............................");
        if (replyToCommId && selectedComm) {
            const topicText =
                selectedComm.topic?.text ||
                selectedComm.topic?.coding?.[0]?.display ||
                null;

            if (topicText) {
                setTopic(topicText);
                console.log("🧵 Topic inherited from previous message:", topicText);
            } else {
                console.warn("⚠️ No topic found on selectedComm.");
            }
        }
    }, [replyToCommId, selectedComm]);

    useEffect(() => {
        async function hydrateInitialRecipients() {
            if (!initialRecipients?.length || !fhirClient) return;

            for (const recipient of initialRecipients) {
                if (recipient.resourceType === "Practitioner") {
                    await fetchProvider(recipient.id, fhirClient, store);
                }
            }
        }

        hydrateInitialRecipients();
    }, [initialRecipients, fhirClient]);


    // Update topic when patient changes
    useEffect(() => {
        //console.log(selectedPatient);
        setSelectedPatient(selectedPatient);
        if (selectedPatient) {
            const patientName = `${selectedPatient.name?.[0]?.given?.join(" ") ?? ""} ${selectedPatient.name?.[0]?.family ?? ""}`.trim();
            const reason = fetchedEncounter?.reasonCode?.[0]?.coding?.[0]?.display;
            const type = fetchedEncounter?.type?.[0]?.text;
            const newTopic = `${patientName || "Progress Update"} - ${reason || type || "Unknown Encounter"}`;
            setTopic(newTopic);
        }

        //console.log(fetchedEncounter);
        //setEncounter(fetchedEncounter);

    }, [selectedPatient, fetchedEncounter]);

    const handleSubmit = async (e) => {
        console.log("Submitting communication...........:");
        e.preventDefault();
        if (!message.trim()) {
            setError("Message content cannot be empty.");
            return;
        }
        let anchorReference = null;


        if (replyToCommId && fhirClient) {
            anchorReference = selectedComm?.partOf?.[0]?.reference || null;
            const topicText = selectedComm?.topic?.text || selectedComm?.topic?.coding?.[0]?.display || null;
            if (topicText) {
                setTopic(topicText);
            } else {
                console.warn("⚠️ No topic found on selectedComm.");
            }
        }


        if (!replyToCommId && fhirClient) {
            try {
                const anchorComm = await fhirClient.create({
                    resourceType: "Communication",
                    status: "completed",
                    topic: {
                        coding: [
                            {
                                system: "http://endpointhealth.ca/custom-topic-system",
                                code: "thread-root",
                                display: topic || "Conversation Thread"
                            }
                        ]
                    },
                    subject: { reference: `Patient/${patientData?.id}` },
                    encounter: { reference: `Encounter/${fetchedEncounter?.id}` },
                    sent: new Date().toISOString(),
                    payload: [{ contentString: "Anchor for new conversation thread." }]
                });

                anchorReference = `Communication/${anchorComm.id}`;
            } catch (err) {
                console.error("Error creating anchor for new conversation:", err);
            }
        }

        const aboutReferences = [];
        console.log("Selected documents:", selectedDocuments);

        for (const [index, doc] of selectedDocuments.entries()) {
            console.log("Processing document:", doc);

            // Detect raw File object and wrap it manually
            const normalizedDoc = (doc instanceof File)
                ? createWrappedMessage({
                    sourceType: "Manual",
                    fileType: doc.type || "application/octet-stream",
                    resourceId: "manual-" + Date.now() + "-" + index,
                    title: doc.name,
                    resource: null,
                    attachment: doc,
                    index
                })
                : doc;

            console.log("Normalized document:", normalizedDoc);

            // Only FHIR resources belong in `about`
            // Only add FHIR resources that are NOT Encounter, Patient, or Practitioner
            if (
                normalizedDoc.resource &&
                !["Encounter", "Patient", "Practitioner"].includes(normalizedDoc.resource.resourceType)
            ) {
                aboutReferences.push({
                    reference: `${normalizedDoc.resource.resourceType}/${normalizedDoc.resource.id}`,
                });

                console.log(`🧩 Added FHIR resource to about: ${normalizedDoc.resource.resourceType}/${normalizedDoc.resource.id}`
                );
            } else if (normalizedDoc.resource) {
                console.log(`⛔ Skipping ${normalizedDoc.resource.resourceType} – disallowed in about[]`
                );
            }


 
        }
        console.log("About references:", aboutReferences);

        const communicationPayload = await buildCommunicationPayload({
            contextProvider,
            recipients,
            selectedPatient,
            encounter: selectedEncounterForModal,
            topic,
            priority,
            category,
            about: aboutReferences,
            reason,
            anchorReference,
            replyToCommId,
            message,
            selectedDocuments
        });
        try {
            console.log("📨 Sending communication payload:", communicationPayload);

            const response = await commService.createCommunication(communicationPayload);
            console.log("📨 Message created:", response);

            if (!response) {
                throw new Error("No response from CommunicationService.");
            }

            // 🧭 Derive anchor ID safely from known sources
            const anchorId =
                anchorReference?.split("/")[1] ||
                replyToCommId ||
                response.partOf?.[0]?.reference?.split("/")[1];

            const anchorRef = `Communication/${anchorId}`;

            // 🚀 Inject optimistic message into thread immediately. ....
            const optimisticMsg = {
                ...response,
                partOf: [{ reference: anchorRef }],
                meta: {
                    isPending: true,
                    sentFromForm: true
                }
            };

            dispatch(addMessage(optimisticMsg));

            // 🔁 Update local thread viewer if wired
            if (typeof onSendLocalReply === "function") {
                onSendLocalReply(optimisticMsg);
            }

            // 🛰️ Fetch any updated server messages in background
            dispatch(promotePending(anchorRef, fhirClient));

            // ✅ UI feedback + cleanup
            setSubmissionResult("✅ Message sent successfully!");
            setMessage("");
            setPriority("routine");
            setCategory("notification");
            setAbout("");
            setReason("");
            setTopic(topic);
            setError("");

            if (!replyToCommId) {
                setSelectedProvider(null);
            }

            if (typeof onCloseForm === "function") {
                onCloseForm();
            }
            // 🎉 Show status modal
            setShowStatusModal(false);
            logCommEvent({ communication: response, store });
       
        } catch (error) {
            console.error("❌ Error sending communication:", error);
            setSubmissionResult("❌ Failed to send message. Please try again.");
        }
    };

    return (
        <Row>
            {/* West: Provider Search */}
            <Col md={4}>
                <Card className="h-100">
                    <Card.Body>
                        <h5>Provider Search</h5>
                        <ProviderSearch
                            fhirClient={fhirClient}
                            patientId={selectedPatient?.id} // <-- Use selectedPatient, not contextPatient
                            onSelect={setSelectedProvider}
                            recipients={recipients}
                            onRecipientsChange={onRecipientsChange}
                        />
                    </Card.Body>
                </Card>
            </Col>
            {/* East: Everything else */}
            <Col md={8}>
                <strong>Recipients:</strong>
                {recipients.length === 0 ? (
                    <div className="text-muted mb-2">No recipients selected</div>
                ) : (
                    <div
                        className="mb-2"
                        style={{
                            maxHeight: "100px",
                            overflowY: "auto",
                            padding: "0.25rem",
                            border: "1px solid #ddd",
                            borderRadius: "4px"
                        }}
                    >
                        {recipients.map((r) => {
                            const provider = r.resourceType === "Practitioner" ? providers?.[r.id] : null;

                            const name =
                                provider?.name?.[0]?.text ||
                                [provider?.name?.[0]?.given?.join(" "), provider?.name?.[0]?.family].filter(Boolean).join(" ") ||
                                r.name?.[0]?.text ||
                                [r.name?.[0]?.given?.join(" "), r.name?.[0]?.family].filter(Boolean).join(" ") ||
                                r.id;

                            return (
                                <Badge
                                    key={r.resourceType + "/" + r.id}
                                    bg="primary"
                                    className="me-1 mb-1 d-inline-flex align-items-center"
                                    style={{ fontSize: "1em", whiteSpace: "nowrap", paddingRight: "0.5rem" }}
                                >
                                    {name}
                                    <FaTimes
                                        onClick={() => removeRecipient(r.id, r.resourceType)}
                                        style={{
                                            marginLeft: "6px",
                                            cursor: "pointer",
                                            fontSize: "0.9em"
                                        }}
                                        title="Remove recipient"
                                    />
                                </Badge>
                            );
                        })}

                    </div>
                )}
                <Card className="mt-0">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <PatientCard
                                    title="Re: Patient"
                                    patientData={selectedPatient}
                                    inContext={inContext}
                                    showSearch={true}
                                    fhirClient={fhirClient}
                                    onSelectPatient={(patient) => setSelectedPatient(patient)}
                                    isReply={isReplyMode}
                                />
                            </Col>
                            <Col md={6}>
                                <EncounterCard
                                    title="Re: Encounter"
                                    encounterData={selectedEncounterForModal}
                                    inContext={inContext}
                                    patientId={selectedPatient?.id}
                                    fhirClient={fhirClient}
                                    enableSearch={true}
                                    isReply={isReplyMode}
                                    onEncounterChange={(enc) => {
                                        setSelectedEncounterForModal(enc);       // ✅ update local state
                                        updateTopicFromEncounter(enc);           // ✅ regenerate topic
                                    }}
                                />
                            </Col>
                        </Row>

                        <Form onSubmit={handleSubmit} className="mt-2">
                            <Row className="mt-0">
                                <Form.Group controlId="topicInput" className="mt-0">
                                    <Form.Label><strong>Topic</strong></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={topic}
                                        onChange={(e) => setTopic(e.target.value)}
                                        placeholder="Enter communication topic"
                                        disabled={!!selectedComm} // ✅ disables input if selectedComm is truthy
                                    />
                                </Form.Group>
                            </Row>


                            <Row className="mt-3">
                                <Col md={6}>
                                    <Form.Group controlId="prioritySelect">
                                        <Form.Label><strong>Priority</strong></Form.Label>
                                        <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                                            <option value="routine">Routine</option>
                                            <option value="urgent">Urgent</option>
                                            <option value="asap">ASAP</option>
                                            <option value="stat">STAT</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="categorySelect" className="mt-0">
                                        <Form.Label><strong>Category</strong></Form.Label>
                                        <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
                                            <option value="alert">Alert</option>
                                            <option value="notification">Notification</option>
                                            <option value="reminder">Reminder</option>
                                            <option value="instruction">Instruction</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group controlId="messageTextArea" className="mt-3">
                                <Form.Label><strong>Message Content</strong></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Type your message here..."
                                />

                                {/* Attachment icons */}
                                {selectedDocuments.length > 0 && (
                                    <div className={styles.attachmentPreviewContainer}>
                                        {selectedDocuments.map((doc, i) => {
                                            const isFile = doc instanceof File;
                                            const title = isFile
                                                ? doc.name.replace(/\.[^/.]+$/, '') // strip extension
                                                : doc.title;

                                            const type = isFile ? doc.type : doc.type || 'unknown';

                                            return (
                                                <div key={i} className={styles.attachmentChip}>
                                                    {type.includes("pdf") ? (
                                                        <FaFilePdf className={styles.icon} />
                                                    ) : type.includes("image") ? (
                                                        <FaFileImage className={styles.icon} />
                                                    ) : (
                                                        <FaFileAlt className={styles.icon} />
                                                    )}
                                                    <span className={styles.label}>{title}</span>
                                                    <button
                                                        className={styles.removeButton}
                                                        onClick={() => handleRemoveAttachment(i)}
                                                        aria-label={`Remove ${title}`}
                                                    >
                                                        ❌
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Form.Group>
                            <Row className="mt-3">
                                <Form.Label><strong>Attachments</strong></Form.Label>
                                <Row>
                                   
                                    <Col md={4}>
                                        <Button variant="outline-secondary" onClick={() => setShowAttachments(true)}>
                                            <FaSearch style={{ marginRight: '6px' }} />
                                            Browse Repository
                                        </Button>
                                    </Col>
                                </Row>
                            </Row>

                            <AttachmentBrowserModal
                                show={showAttachments}
                                onClose={() => setShowAttachments(false)}
                                selectedPatient={selectedPatient}
                                fhirClient={fhirClient}
                                setSelectedDocuments={setSelectedDocuments}
                            />

                            {error && <p className="text-danger mt-2">{error}</p>}
                            <Button
                                type="submit"
                                variant="primary"
                                className="mt-3"
                                onClick={handleSubmit}
                                disabled={recipients.length === 0} // Disable if no providers selected
                            >
                                Send
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>
            {/* Modal for Submission Status */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Submission Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {submissionResult}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={() => setShowStatusModal(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </Row>
    );
}

export default CommunicationForm;